import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslations } from "next-intl";
import { IconEyeOff } from "@tabler/icons-react";
import { useToast } from "@/components/Toast/Toast";
import { endBlocks, usePreviewEndedStore } from "@/lib/preview/previewEndedStore";

/** Indirection for the reload, so a test can observe it (jsdom's `location`
 *  is unforgeable). */
export const noticeActions = {
  reload: () => window.location.reload(),
};

/**
 * B2-0 (decision 3, PB-FB-H3-PREV-BUILDERSTOP): what a host shows when the
 * preview ended LOCALLY (`endPreviewLocally`: the expiry self-stop, or a 401
 * `sessionExpired` on the current bearer — since revokes take effect, every
 * builder tab opened from the preview loses its bearer as soon as the main tab
 * edits or switches).
 *
 * The stop remounts the host's subtree as the ADMIN. A tab that did not mint
 * the session (a builder or copy tab) would then save as the admin, silently,
 * while its user still believes it is a read-only preview — so:
 *  - builder host of the previewed event: ALWAYS a blocking notice;
 *  - shell host of the previewed event: blocking in a copy tab, and in EVERY
 *    tab after an UNPLANNED end (the bearer died, `sessionExpired`: the
 *    request tombstone refuses the event's requests until a reload — audit r1
 *    F1, one rule for main and copy tabs); a toast only in the tab that minted
 *    the session after its planned expiry self-stop (its outlet visibly
 *    remounts as the admin and keeps working);
 *  - any other host (a template builder without `:eventId`, another event's
 *    builder or shell): never blocks — a reload modal over real, possibly
 *    unsaved work would be wrong there (LOW-1); it acknowledges only an end
 *    that does not block (the minting tab's planned expiry). An end that
 *    BLOCKS (`endBlocks`) lives as long as its request tombstone (audit r2
 *    F1): acknowledged by another event's host, the user came back to the
 *    ended event with every request refused and no notice. A refused request
 *    raises it again too (`reraisePreviewEnded`).
 *
 * Mount it OUTSIDE the preview branch and BEFORE the `<Outlet/>`: passive
 * effects run in sibling order, so the notice's keydown capture is registered
 * before the as-admin remounted subtree's listeners (R3-F3), and a branch switch
 * does not remount it (R3-FI1).
 */
export function PreviewEndedNotice({
  host,
  eventId,
}: {
  host: "shell" | "builder";
  /** The host's event (shell `eventId`, builder `useParams().eventId`). */
  eventId: string | null;
}) {
  const t = useTranslations("rolePreview");
  const { showToast } = useToast();
  const ended = usePreviewEndedStore((s) => s.ended);
  const matches = !!ended && !!eventId && ended.eventId === eventId;
  const blocking = matches && (host === "builder" || endBlocks(ended));

  useEffect(() => {
    // Read the STORE, not the render closure: under StrictMode the effect runs
    // twice and the second run must see the ack of the first (one toast).
    const cur = usePreviewEndedStore.getState().ended;
    if (!cur) return;
    const mine = !!eventId && cur.eventId === eventId;
    if (mine && (host === "builder" || endBlocks(cur))) {
      return; // blocks until reload
    }
    // Another event's host: a blocking end is not ours to clear (audit r2 F1).
    if (!mine && endBlocks(cur)) return;
    if (mine) showToast(t("sessionEnded"), "info");
    usePreviewEndedStore.getState().ack();
  }, [ended, eventId, host, showToast, t]);

  if (!blocking) return null;
  return <BlockingNotice />;
}

/**
 * The real lock (MED-1 R2, R3-F2, R3-F4): portal into ONE wrapper under `body`
 * at `z-[100010]` (above the preview chrome 10001, toasts 10002, modals up to
 * 10070 and the website builder's body portals at 100000); every other body
 * child `inert` (all `createPortal`s go to `body`, so `#root` alone is not
 * enough), later ones too via a MutationObserver; a window keydown CAPTURE with
 * a focus trap. Reload keys stay free — reloading is the only way out. On
 * unmount only its own `inert` is removed, the observer and listener go.
 */
function BlockingNotice() {
  const tPreview = useTranslations("rolePreview");
  const tErrors = useTranslations("errors");
  const textId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [wrapper] = useState(() => {
    const el = document.createElement("div");
    el.setAttribute("data-preview-ended-root", "");
    return el;
  });

  useLayoutEffect(() => {
    document.body.appendChild(wrapper);
    const marked = new Set<Element>();
    const lock = (el: Element) => {
      if (el === wrapper || el.contains(wrapper)) return;
      if (el.hasAttribute("inert")) return; // someone else's — never ours to undo
      el.setAttribute("inert", "");
      marked.add(el);
    };
    Array.from(document.body.children).forEach(lock);
    const observer = new MutationObserver((records) => {
      for (const r of records) {
        r.addedNodes.forEach((n) => {
          if (n instanceof Element && n.parentElement === document.body) lock(n);
        });
      }
    });
    observer.observe(document.body, { childList: true });

    const onKeyDown = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      const key = e.key;
      // Reload: never preventDefault (Chromium honours it for F5 / Cmd+R too).
      if (key === "F5" || (mod && (key === "r" || key === "R"))) {
        e.stopImmediatePropagation();
        return;
      }
      if (key === "Escape" || (mod && (key === "s" || key === "S"))) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }
      if (key === "Tab") {
        e.preventDefault();
        e.stopImmediatePropagation();
        buttonRef.current?.focus();
        return;
      }
      // Everything else: no app listener sees it; the default action (Enter /
      // Space on the button) still runs, and behind `inert` nothing is focusable.
      e.stopImmediatePropagation();
    };
    window.addEventListener("keydown", onKeyDown, true);
    buttonRef.current?.focus();

    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      observer.disconnect();
      for (const el of marked) el.removeAttribute("inert");
      marked.clear();
      wrapper.remove();
    };
  }, [wrapper]);

  // A click beside the button must not move focus to <body> (then every key
  // would be "outside" and the trap would have to guess).
  const keepFocus = (e: React.MouseEvent) => e.preventDefault();

  return createPortal(
    <div
      data-testid="preview-ended-notice"
      className="fixed inset-0 z-[100010] flex items-center justify-center bg-black/40 px-4"
      onMouseDown={keepFocus}
      role="presentation"
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={textId}
        onMouseDown={keepFocus}
        className="w-full max-w-100 surface-primary rounded-2xl shadow-xl p-6 flex flex-col gap-4"
      >
        <div className="flex items-start gap-3">
          <span className="shrink-0 surface-tertiary rounded-full p-2">
            <IconEyeOff size={18} className="font-secondary" />
          </span>
          <p id={textId} className="body-2-medium font-primary">
            {tPreview("sessionEnded")}
          </p>
        </div>
        <div className="flex items-center justify-end">
          <button
            ref={buttonRef}
            type="button"
            data-testid="preview-ended-reload"
            onClick={() => noticeActions.reload()}
            className="cta-primary font-light-primary body-2-medium rounded-lg px-3 py-2"
          >
            {tErrors("reload")}
          </button>
        </div>
      </div>
    </div>,
    wrapper,
  );
}
